/* Phase 4 §7 — pointer input.
 *
 * The engine owns hit-testing; the UI only forwards device-pixel coordinates.
 * pointerdown places the caret, drag extends the selection, double-click
 * selects a word. Caret placement and extension are single-hop commands
 * (PLACE_CARET_AT_POINT / EXTEND_SELECTION_TO_POINT — issues #53/#64) that
 * hit-test and move the selection inside ONE serialized dispatch; the engine
 * answers with SELECTION_CHANGED, which engine-store fans out to the
 * overlays. */
import type { EngineClient } from '../engine/engine-client';
import type { Point } from '../engine/types';
import {
    PAGE_GAP_PT,
    PAGE_H_PT,
    deviceRatio,
    paintScale,
    editingStoryForPointer,
    enginePageTopDevice,
    headerFooterZoneAt,
    selectImageByPoint,
    selectionViewForPointer,
} from '../state/engine-store';

/** Issue #80 — footnote / endnote stories ride the header/footer story
 *  protocol (`editing_story`) but live in the body's page flow. Issue
 *  #83 — text-box stories too: the engine routes every press itself
 *  (inside a box → that box, elsewhere → back to the body). */
function isNoteArea(area: string): boolean {
    return area === 'Footnote' || area === 'Endnote' || area === 'TextBox';
}

/**
 * Wire pointer listeners on `canvas` for one page in the multi-canvas DOM
 * architecture (Phase 6c). Returns a teardown that removes them. Pointer
 * events still fire on a `<canvas>` whose drawing surface has been
 * transferred to the worker, so this is safe to attach post-transfer.
 *
 * `pageIdx` is the canvas's page index. Hit-tests ship the click in this
 * page's LOCAL device-pixel coords via `HIT_TEST_IN_PAGE`; the engine adds
 * the page's REAL accumulated top offset (per-section landscape / custom
 * page sizes included), so the TS side does no page-height math at all.
 */
export function attachPointer(
    canvas: HTMLCanvasElement,
    client: EngineClient,
    pageIdx: number = 0,
): () => void {
    let dragging = false;
    /* Bumped on every new gesture. Issue #64 made the drag path
       (extendTo) single-hop like plain-click placement, so the worker
       queue orders every pointer command itself and no in-flight
       hit-test can resolve out of order any more; the counter survives
       only as the gesture identity the zone / image gates bump. */
    let gesture = 0;

    /* Client coords → this page's LOCAL engine device pixels (origin at
       the page's top-left). `HIT_TEST_IN_PAGE` adds the page's real
       accumulated top offset engine-side. `e.clientY - r.top`
       legitimately exceeds the canvas height (or goes negative) when a
       `setPointerCapture` drag wanders onto a neighbouring canvas — the
       overshoot maps onto the adjacent page because the CSS inter-page
       gap mirrors the engine's `PAGE_GAP_PT`. */
    const toLocal = (e: PointerEvent | MouseEvent): Point => {
        const r = canvas.getBoundingClientRect();
        const dpr = deviceRatio();
        return {
            x: (e.clientX - r.left) * dpr,
            y: (e.clientY - r.top) * dpr,
        };
    };

    /* Client coords → DOCUMENT-ABSOLUTE engine device pixels, only for
       the gesture commands that have no page-local bridge variant
       (`SELECT_WORD_AT` / `SELECT_PARAGRAPH_AT` / `SELECT_CELL_AT`).
       The page-top offset is the shared uniform-A4 approximation from
       engine-store — exact for portrait-A4 documents; per-section page
       heights are tracked separately. */
    const toGlobal = (e: PointerEvent | MouseEvent): Point => {
        const local = toLocal(e);
        /* Engine-reported page top (device px, exact under mixed
           orientations — issue #26); uniform-A4 fallback pre-paint. */
        const pageOffsetY =
            enginePageTopDevice(pageIdx) ??
            pageIdx * (PAGE_H_PT + PAGE_GAP_PT) * paintScale();
        return { x: local.x, y: local.y + pageOffsetY };
    };

    /* Issue #53 — single-hop caret placement, posted SYNCHRONOUSLY (no
       await between the pointer event and the dispatch). The engine
       hit-tests AND sets the collapsed selection inside ONE serialized
       command, so a keystroke fired <5 ms after the click still enters
       the worker queue AFTER it and inserts at the clicked position.
       The old two-hop HIT_TEST_IN_PAGE → SET_SELECTION needed the
       gesture-staleness guard here; single-hop placements are ordered
       by the queue itself (two single clicks always resolve before the
       dblclick's SELECT_WORD_AT that follows them). */
    const placeCaret = (at: Point): void => {
        void client
            .dispatch({ type: 'PLACE_CARET_AT_POINT', page: pageIdx, at })
            .catch((e: unknown) => {
                console.error('placeCaret failed', e);
            });
    };

    /* Issue #64 — drag / shift-click extension, posted SYNCHRONOUSLY
       like placeCaret: the engine hit-tests AND extends in one
       serialized command, so a keystroke fired right after a
       shift-click enters the queue behind it and replaces the EXTENDED
       selection. The old HIT_TEST_IN_PAGE → (await) → EXTEND_SELECTION
       let that keystroke execute against the pre-extension selection. */
    const extendTo = (at: Point): void => {
        void client
            .dispatch({ type: 'EXTEND_SELECTION_TO_POINT', page: pageIdx, at })
            .catch((e: unknown) => {
                console.error('extendSelection failed', e);
            });
    };

    const onPointerDown = (e: PointerEvent): void => {
        /* Non-primary buttons never start a drag (issue #36) — treating a
           right-click like a left-click collapsed the active TABLE_CELLS
           selection before TableContextMenu could read it, leaving "Merge
           cells" permanently disabled. Word semantics for the right
           button: a press INSIDE the active selection (or anywhere while
           a TABLE_CELLS selection is live — its rects cover only the text
           runs, so point-in-rect underreports cell coverage) preserves
           the selection for the context menu; a press OUTSIDE a linear
           selection moves the caret so menu actions never target a stale
           far-away position. Middle clicks are ignored entirely. Touch
           and pen contacts report button 0, so both still place the
           caret. */
        if (e.button !== 0) {
            if (e.button === 2) {
                const sel = selectionViewForPointer();
                if (sel.kind.kind !== 'TABLE_CELLS') {
                    const dpr = deviceRatio();
                    const g = toGlobal(e);
                    const cx = g.x / dpr;
                    const cy = g.y / dpr;
                    const inside = sel.rects.some(
                        (r) =>
                            cx >= r.x &&
                            cx <= r.x + r.w &&
                            cy >= r.y &&
                            cy <= r.y + r.h,
                    );
                    if (!inside) {
                        gesture += 1;
                        placeCaret(toLocal(e));
                    }
                }
            }
            return;
        }
        /* Phase 3 (#39) — header/footer zone gate, BEFORE any caret
           placement (mirrors the image-hit early-return below). Rules:
           - body mode + press in a band → an ordinary body press
             (issue #510, Word / the OnlyOffice methods memo §2: a single
             click in a margin band resolves to the nearest body line;
             the dblclick that follows enters the band, and Esc later
             restores that caret). It used to be swallowed so the body
             caret survived for the entry stash — which also left the
             press without any visible effect;
           - story mode + press in the ACTIVE story's own band on its
             anchor page → fall through (caret placement inside the
             band; the engine clamps to the story);
           - story mode + press in ANY OTHER band → RE-ANCHOR there
             (issue #70 / design review M6: linked parts span many
             pages; the anchor-page-only identity test silently ate
             clicks on every other instance — Word switches the edited
             band on click, so dispatch EnterHeaderFooter for the
             pressed page/area instead);
           - story mode + press on the dimmed body → swallow (dblclick
             exits — Word semantics);
           - issue #80 NOTE story mode (footnote / endnote) → a press in
             a margin band is swallowed (dblclick enters the band); any
             other press falls through to
             PLACE_CARET_AT_POINT, which the engine routes itself
             (inside a note band → that note, elsewhere → back to the
             body at the point). */
        {
            const local = toLocal(e);
            const zone = headerFooterZoneAt(pageIdx, local.y);
            const story = editingStoryForPointer();
            if (story && isNoteArea(story.area)) {
                if (zone !== null) {
                    gesture += 1;
                    return;
                }
            } else if (story) {
                const inOwnBand =
                    zone === story.area && pageIdx === story.page;
                if (!inOwnBand) {
                    gesture += 1;
                    if (zone !== null) {
                        void client.dispatch({
                            type: 'ENTER_HEADER_FOOTER',
                            page: pageIdx,
                            area: zone,
                        });
                    }
                    return;
                }
            }
        }
        /* Issue #44 — a primary press on an inline image body selects
           the image (showing resize handles) instead of placing a caret
           or starting a text drag. Presses on the handles themselves
           never reach the canvas — the overlay divs sit above it and
           consume the event. `selectImageByPoint` also CLEARS a prior
           image selection when the click misses every image, so a normal
           text click deselects. Coordinates are document-absolute CSS
           px, matching the stored image rects. Skipped in header / footer
           / note story mode — body images are unreachable behind the dim.
           Issue #206 — while a text box is being edited, a press on a
           picture INSIDE a text box still selects it (the engine accepts
           picture edits in text-box mode); a press elsewhere falls
           through to the story's own routing. */
        const imageStory = editingStoryForPointer();
        if (!imageStory || imageStory.area === 'TextBox') {
            const dpr = deviceRatio();
            const gCss = toGlobal(e);
            if (selectImageByPoint(gCss.x / dpr, gCss.y / dpr, imageStory !== undefined)) {
                gesture += 1;
                return;
            }
        }
        canvas.setPointerCapture(e.pointerId);
        dragging = true;
        gesture += 1;
        /* Shift+Click extends the existing selection (anchor stays
           put, caret jumps to the hit position) instead of resetting
           it. UX_BEHAVIOR_SPEC §IV.7. */
        if (e.shiftKey) {
            extendTo(toLocal(e));
            return;
        }
        placeCaret(toLocal(e));
    };

    const onPointerMove = (e: PointerEvent): void => {
        if (!dragging) return;
        extendTo(toLocal(e));
    };

    const onPointerUp = (e: PointerEvent): void => {
        dragging = false;
        if (canvas.hasPointerCapture(e.pointerId)) {
            canvas.releasePointerCapture(e.pointerId);
        }
    };

    const onDblClick = (e: MouseEvent): void => {
        /* Bump past the two single-clicks' in-flight hit-tests so their
           SET_SELECTIONs are dropped and the word selection survives. */
        gesture += 1;
        /* Phase 3 (#39) — double-click routing (the Word affordance):
           - a band while NOT already editing that exact band+page →
             ENTER_HEADER_FOOTER for this page's section (the engine
             materialises an absent part, like the toolbar button);
           - the body content area while a story is active → exit AND
             place the body caret at the click (issue #510, Word / the
             OnlyOffice methods memo §2) — both posted synchronously, so
             the worker queue applies them in order and a keystroke
             queued behind them types at the clicked spot;
           - the ACTIVE band → fall through to word selection (the
             critic-flagged hijack: in-band dblclick must keep its
             editing affordance). */
        const zone = headerFooterZoneAt(pageIdx, toLocal(e).y);
        const story = editingStoryForPointer();
        const inOwnBand =
            story !== undefined && zone === story.area && pageIdx === story.page;
        if (zone !== null && !inOwnBand) {
            void client
                .dispatch({ type: 'ENTER_HEADER_FOOTER', page: pageIdx, area: zone })
                .catch((err: unknown) => {
                    console.error('enterHeaderFooter failed', err);
                });
            return;
        }
        /* Issue #80 — inside a note story a body double-click selects a
           word like anywhere else (the note lives in the page flow; a
           single click already routes story ↔ body engine-side). */
        if (story && zone === null && !isNoteArea(story.area)) {
            void client.dispatch({ type: 'EXIT_HEADER_FOOTER' }).catch((err: unknown) => {
                console.error('exitHeaderFooter failed', err);
            });
            placeCaret(toLocal(e));
            return;
        }
        void client.dispatch({ type: 'SELECT_WORD_AT', at: toGlobal(e) });
    };

    /* Triple-click — whole paragraph. Quadruple-click — whole cell
       (when inside a table) or whole document (UX_BEHAVIOR_SPEC §I.3).
       DOM has no native triple/quadruple events; `e.detail` carries
       the click count. Bump `gesture` on each so the pending
       hit-tests from the inner clicks get dropped (mirrors dblclick's
       defence against the two single-clicks). `SELECT_CELL_AT`
       resolves the click's owning cell engine-side and falls back to
       `SELECT_ALL` when the hit lands outside any cell. */
    const onClick = (e: MouseEvent): void => {
        if (e.detail === 3) {
            gesture += 1;
            void client.dispatch({ type: 'SELECT_PARAGRAPH_AT', at: toGlobal(e) });
        } else if (e.detail >= 4) {
            gesture += 1;
            void client.dispatch({ type: 'SELECT_CELL_AT', at: toGlobal(e) });
        }
    };

    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('dblclick', onDblClick);
    canvas.addEventListener('click', onClick);

    return () => {
        canvas.removeEventListener('pointerdown', onPointerDown);
        canvas.removeEventListener('pointermove', onPointerMove);
        canvas.removeEventListener('pointerup', onPointerUp);
        canvas.removeEventListener('dblclick', onDblClick);
        canvas.removeEventListener('click', onClick);
    };
}
